import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"
import FormSimple from "@components/formGroup/formSimple"
import InputWithAddOptions from "@components/input/InputWithAddOptions"
import { CheckboxSimple, Input } from "@components/tailwindComponents"
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe"
import ListboxHierarchical from "@components/tailwindComponents/ListboxHierarchical"
import ListboxSimple from "@components/tailwindComponents/ListboxSimple"
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker"
import { usePoste } from "@hooks/settings"
import CustomTooltip from "@components/tooltip/index"
import IconInfo from "@image/icones/DocumentsEmployers/iconInfo.svg"
// import { EmployerSalary } from "@services/all.services"
import useCheckDuplicationUsers from "@pages/shared/useCheckDuplicationUsers";

export const useSectionJobColumns = () => {
  const { t } = useTranslation("pages.employés.emploi&Salary")

  return useMemo(() => [
    { id: "position", label: t("Poste*") },
    { id: "hierarchical", label: t("Supérieur hiérarchique*") },
    { id: "location", label: t("Emplacement*") },
    { id: "group", label: t("Groupe") },
    { id: "numEmployer", label: t("Numéro de l'employé") },
    { id: "hiringDate", label: t("Date d'embauche") },
    { id: "seniorityDate", label: t("Date d'ancienneté relative") }
  ], [t])
}

export const SectionJobTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary")
  return (
    <>
      <th
        key="position"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.position ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Poste*")}
      </th>
      <th
        key="hierarchical"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.hierarchical ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Supérieur hiérarchique*")}
      </th>
      <th
        key="location"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.location ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Emplacement*")}
      </th>
      <th
        key="group"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.group ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Groupe")}
      </th>
      <th
        key="numEmployer"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.numEmployer ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Numéro de l'employé")}
      </th>
      <th
        key="hiringDate"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.hiringDate ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Date d'embauche")}
      </th>
      <th
        key="seniorityDate"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.seniorityDate ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Date d'ancienneté relative")}
      </th>
      <th
        key="defaultDateHiring"
        className="arh-w-[0.75rem] arh-bg-greyColor arh-px-1 arh-py-[10px] arh-align-bottom"
      />
      <th
        key="info"
        className="arh-w-[0.75rem] arh-bg-greyColor arh-px-1 arh-py-[10px] arh-align-bottom"
      />
    </>
  )
}

export const SectionJobTableBulkCells = ({
  control,
  employeeIndex,
  employee,
  readOnly = false,
  superiorReadonly = false,
  postes = [],
  employeList = [],
  emplacementsList = [],
  groupesList = [],
  setSuperZIndex,
  trigger,
  mustWait,
  getValues,
  hiddenColumns = {},
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary")
  const { _insertPoste, _editPoste, _deletePoste } = usePoste()
  const {
    numAlreadyExist
  } = useCheckDuplicationUsers(
    {
      employeeId: employee._id
    }
  )


  // Règles de validation conditionnelles
  const makeSeniorityDateRule = useCallback(
    () => ({
      validate: {
        anciennete_required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`)
          if (!isSelected) return true
          const defaultDateHiring = getValues(`employees.${employeeIndex}.emploiSalary.job.defaultDateHiring`)
          return Boolean(!defaultDateHiring || value)
        },
      },
    }),
    [getValues, employeeIndex],
  )

  const makeNumEmployerRule = useCallback(
    () => ({
      validate: {
        numAlreadyExist: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`)
          if (!isSelected) return true
          return numAlreadyExist(value)
        },
      },
    }),
    [getValues, employeeIndex, numAlreadyExist],
  )

  return (
    <>
      <td
        key="position"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.position ? "arh-hidden" : ""}`}
      >
        <div className="arh-flex arh-flex-col">
          {mustWait || readOnly ? (
            <Input
              value={employee.emploiSalary?.Poste?.title || ""}
              placeholder={mainTranslator("Poste*")}
              disabled
              additionnalClas="!arh-py-[5px] !arh-px-2.5"
            />
          ) : (
            <Controller
              control={control}
              name={`employees.${employeeIndex}.emploiSalary.Poste`}
              defaultValue={employee.emploiSalary?.Poste || null}
              render={({ field: { onChange, value, name } }) => (
                <InputWithAddOptions
                  name={name}
                  defaultText={mainTranslator("Poste*")}
                  optionsList={postes}
                  secondDefaultText={mainTranslator("Nom poste")}
                  value={value?.title || ""}
                  optionListLabel="title"
                  onChange={(selectedOption) => {
                    onChange(selectedOption)
                  }}
                  addItem={_insertPoste}
                  editItem={_editPoste}
                  deleteItem={_deletePoste}
                  hideEdit={true}
                  disableInputChange={true}
                  classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                  classBtn="arh-absolute arh-right-1 arh-top-1/2 -arh-translate-y-1/2 !arh-py-[0.0625rem] !arh-px-2.5"
                  withSearch
                  searchInputClass="!arh-py-[5px] !arh-px-2.5"
                />
              )}
            />
          )}
        </div>
      </td>
      <td
        key="hierarchical"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-ml-[1rem] ${hiddenColumns.hierarchical ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.hierarchical`}
          defaultValue={employee.emploiSalary?.job?.hierarchical || ""}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <ListboxHierarchical
              currentEmployeId={employee?._id}
              placeholder={mainTranslator("Supérieur hiérarchique*")}
              optionList={employeList}
              selectedValue={value}
              setSelectedValue={onChange}
              readOnly={mustWait || readOnly || superiorReadonly}
              customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
              searchInputClass="!arh-py-[5px] !arh-px-2.5"
              customItemWidth="!arh-min-w-24"
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="location"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-ml-[1rem] ${hiddenColumns.location ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.emplacement`}
          defaultValue={employee.emploiSalary?.job?.emplacement || []}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <ListboxSimple
              placeholder={mainTranslator("Emplacement*")}
              optionList={emplacementsList}
              selectedValue={value}
              setSelectedValue={onChange}
              multiple
              readOnly={mustWait || readOnly}
              customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="group"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-ml-[1rem] ${hiddenColumns.group ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.groupIds`}
          defaultValue={employee.groupIds || []}
          render={({ field: { onChange, value } }) => {
            return (
              <ListboxGroupe
                placeholder={mainTranslator("Groupe")}
                optionList={groupesList}
                selectedValue={value}
                setSelectedValue={(newIds) => {
                  onChange(newIds)
                }}
                readOnly={mustWait || readOnly}
                customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
              />
            )
          }}
        />
      </td>
      <td
        key="numEmployer"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-ml-[1rem] ${hiddenColumns.numEmployer ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.numEmployer`}
          defaultValue={employee.emploiSalary?.job?.numEmployer || ""}
          rules={makeNumEmployerRule()}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              placeholder={mainTranslator("Numéro de l'employé")}
              value={value}
              onChange={(e) => {
                onChange(e.target.value)
              }}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5"
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="hiringDate"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.hiringDate ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.hiringDate`}
          defaultValue={employee.emploiSalary?.job?.hiringDate || ""}
          render={({ field: { onChange, value, ref }, fieldState: { invalid } }) => (
            <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-flex arh-w-full" ref={ref}>
              {mustWait || readOnly ? (
                <Input
                  value={employee.emploiSalary?.job?.hiringDate || ""}
                  placeholder={mainTranslator("Date d'embauche")}
                  disabled
                  additionnalClas="!arh-py-[5px] !arh-px-2.5"
                />
              ) : (
                <DefaultDatePicker
                  placeholder={mainTranslator("Employé depuis")}
                  dateValue={value}
                  setDateValue={onChange}
                  setSuperZIndex={setSuperZIndex}
                  className="focus_green dateOfBirth"
                  inputDatePicker="form-control !arh-h-[calc(2rem+2px)] !arh-py-[5px] !arh-px-2.5"
                  invalid={invalid}
                />
              )}
            </div>
          )}
        />
      </td>
      <td
        key="seniorityDate"
        className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.seniorityDate ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.seniorityDate`}
          defaultValue={employee.emploiSalary?.job?.seniorityDate || ""}
          rules={makeSeniorityDateRule()}
          render={({ field: { onChange, value, ref }, fieldState: { invalid } }) => (
            <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-flex arh-w-full" ref={ref}>
              {mustWait || readOnly ? (
                <Input
                  value={employee.emploiSalary?.job?.seniorityDate || ""}
                  placeholder={mainTranslator("Date de naissance")}
                  disabled
                  additionnalClas="!arh-py-[5px] !arh-px-2.5"
                />
              ) : (
                <DefaultDatePicker
                  placeholder={mainTranslator("Date d'ancienneté")}
                  dateValue={value}
                  setDateValue={onChange}
                  setSuperZIndex={setSuperZIndex}
                  className="focus_green dateOfBirth"
                  inputDatePicker="form-control !arh-h-[calc(2rem+2px)] !arh-py-[5px] !arh-px-2.5"
                  invalid={invalid}
                />
              )}
            </div>
          )}
        />
      </td>
      <td
        key="defaultDateHiring"
        className="arh-w-[0.75rem] arh-px-1 arh-align-middle arh-font-poppins arh-text-xs arh-text-darkGreyColor"
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.emploiSalary.job.defaultDateHiring`}
          defaultValue={employee.emploiSalary?.job?.defaultDateHiring || false}
          render={({ field: { onChange, value } }) => (
            <CheckboxSimple
              id="defaultDateHiring"
              checked={value}
              disabled={mustWait || readOnly}
              onChange={(e) => {
                onChange(e.target.checked)
                if (!e.target.checked) {
                  trigger(`employees.${employeeIndex}.emploiSalary.job.seniorityDate`)
                }
              }}
            />
          )}
        />
      </td>
      <td
        key="info"
        className="arh-w-[0.75rem] arh-px-1 arh-align-middle arh-font-poppins arh-text-xs arh-text-darkGreyColor text-center"
      >
        <CustomTooltip
          withImage={true}
          iconInfo={IconInfo}
          classDrop="!arh-w-[20.188rem] !arh-left-48 !arh-p-5"
          fixedPosition={true}
          className="arh-pointer-events-none !arh-m-0 arh-flex-col"
          content={
            <div className="arh-w-full">
              <div className="arh-text-[11px] arh-font-medium">
                {mainTranslator("Utiliser comme date d'embauche dans les statistiques et anniversaires")}
              </div>
            </div>
          }
        />
      </td>
    </>
  )
}

export const getJobColumnCount = () => 9
